import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { SearchOverlayService } from '../core/search/search-overlay.service';
import { SearchStateService } from '../core/search/search-state.service';

@Component({
  selector: 'gg-search-modals',
  imports: [TranslocoModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ng-container *transloco="let t">
      <!-- DATE MODAL -->
      @if (overlay.showCal()) {
        <div
          class="gg-fade"
          style="position:fixed;inset:0;z-index:60;background:rgba(20,24,32,.42);display:flex;align-items:center;justify-content:center;padding:20px"
          (click)="overlay.toggleCal()"
        >
          <div
            class="gg-pop"
            style="background:#fff;border-radius:20px;box-shadow:0 30px 80px -18px rgba(0,0,0,.5);padding:22px;width:362px;max-width:100%"
            (click)="$event.stopPropagation()"
          >
            <div style="display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:4px">
              <div style="font-size:17px;font-weight:800;color:#16213c">{{ t('search.chooseDates') }}</div>
              <div style="cursor:pointer;color:#8a93a3;font-size:22px;line-height:1" (click)="overlay.toggleCal()">×</div>
            </div>
            <div style="font-size:12.5px;color:#8a93a3;margin-bottom:16px">{{ t('search.datesNights', { dates: state.datesLabel(), count: state.nights() }) }}</div>
            <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px">
              <div class="btn-outline" style="width:32px;height:32px;border-radius:9px;border:1px solid #e7ddc9;display:flex;align-items:center;justify-content:center;font-weight:700;color:#16213c" (click)="overlay.prevMonth()">‹</div>
              <div style="font-weight:700;font-size:15px;color:#16213c">{{ overlay.calTitle() }}</div>
              <div class="btn-outline" style="width:32px;height:32px;border-radius:9px;border:1px solid #e7ddc9;display:flex;align-items:center;justify-content:center;font-weight:700;color:#16213c" (click)="overlay.nextMonth()">›</div>
            </div>
            <div style="display:grid;grid-template-columns:repeat(7,1fr);gap:2px;margin-bottom:4px">
              @for (wd of overlay.weekdays(); track wd) {
                <div style="text-align:center;font-size:11px;font-weight:700;color:#9aa3b2;padding:4px 0">{{ wd }}</div>
              }
            </div>
            <div style="display:grid;grid-template-columns:repeat(7,1fr);gap:2px">
              @for (d of overlay.calDays(); track $index) {
                <div
                  (click)="overlay.pickDay(d)"
                  [style.aspect-ratio]="'1'"
                  style="display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:600;border-radius:9px"
                  [style.cursor]="d.selectable ? 'pointer' : 'default'"
                  [style.color]="d.selected ? '#fff' : d.unavailable ? '#c7ccd4' : !d.selectable ? '#cdd3dc' : '#3a4250'"
                  [style.background]="d.selected ? '#16213c' : d.inRange ? '#efeae0' : 'transparent'"
                  [style.text-decoration]="d.unavailable ? 'line-through' : 'none'"
                  [title]="d.unavailable ? t('search.unavailable') : ''"
                >{{ d.inMonth ? d.label : '' }}</div>
              }
            </div>
            <div class="btn-ink" style="margin-top:16px;border-radius:12px;height:46px;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:14.5px" (click)="overlay.toggleCal()">{{ t('search.done') }}</div>
          </div>
        </div>
      }

      <!-- GUESTS MODAL -->
      @if (overlay.showGuests()) {
        <div
          class="gg-fade"
          style="position:fixed;inset:0;z-index:60;background:rgba(20,24,32,.42);display:flex;align-items:center;justify-content:center;padding:20px"
          (click)="overlay.toggleGuests()"
        >
          <div
            class="gg-pop"
            style="background:#fff;border-radius:20px;box-shadow:0 30px 80px -18px rgba(0,0,0,.5);padding:22px;width:320px;max-width:100%"
            (click)="$event.stopPropagation()"
          >
            <div style="display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:18px">
              <div style="font-size:17px;font-weight:800;color:#16213c">{{ t('search.howMany') }}</div>
              <div style="cursor:pointer;color:#8a93a3;font-size:22px;line-height:1" (click)="overlay.toggleGuests()">×</div>
            </div>
            <div style="display:flex;align-items:center;justify-content:space-between">
              <div>
                <div style="font-weight:700;font-size:15px;color:#16213c">{{ t('search.guests') }}</div>
                <div style="font-size:12.5px;color:#8a93a3">{{ t('search.guestsHint') }}</div>
              </div>
              <div style="display:flex;align-items:center;gap:16px">
                <div style="width:36px;height:36px;border-radius:50%;border:1.5px solid #d8cfbf;display:flex;align-items:center;justify-content:center;cursor:pointer;font-size:20px;color:#16213c" (click)="state.decGuest()">−</div>
                <div style="min-width:20px;text-align:center;font-weight:800;font-size:17px;color:#16213c">{{ state.guests() }}</div>
                <div style="width:36px;height:36px;border-radius:50%;border:1.5px solid #d8cfbf;display:flex;align-items:center;justify-content:center;cursor:pointer;font-size:20px;color:#16213c" (click)="state.incGuest()">+</div>
              </div>
            </div>
            <div class="btn-ink" style="margin-top:22px;border-radius:12px;height:46px;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:14.5px" (click)="overlay.toggleGuests()">{{ t('search.done') }}</div>
          </div>
        </div>
      }
    </ng-container>
  `,
})
export class SearchModalsComponent {
  readonly overlay = inject(SearchOverlayService);
  readonly state = inject(SearchStateService);
}
